import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { SetPassword } from 'components/admin';
import { CopyButton, MoreOptions, SendEmail } from 'components/common';
import {
  UsersDelete,
  UsersDisableTwoFactorAuth,
  UsersEdit,
  UsersSetLocked,
  UsersUpdatePasswordSet
} from '.';

import type { PageState } from 'types';
import type { EmailTemplateData } from 'shared-types';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  onUserDeleted: () => Promise<void>;
  onUserUpdated: () => Promise<void>;
  targetUser: PageState['users'][0];
}

const UsersDropdown = ({
  emailTemplates,
  emailTemplatesDefault,
  onUserDeleted,
  onUserUpdated,
  targetUser
}: Props) => {
  const { dispatch } = useDispatch();
  const { language, messages, user: loggedInUser } = useReduxState();

  const handleCopy = async () => {
    const response = await api.get(
      `/admin/users/${targetUser.id}/activation-link`
    );
    const { url } = response.data;
    return url;
  };

  const handleSendEmail = async ({
    email,
    targetUserEmailAddress
  }: {
    email: EmailTemplateData['email'];
    targetUserEmailAddress: string;
  }) => {
    try {
      const response = await api.post(`/admin/users`, {
        emails: [targetUserEmailAddress],
        emailContent: email,
        language,
        isResendEmail: true
      });

      if (!response.data.success) {
        throw new Error('error');
      } else if (response.data.errors?.email_not_registered?.length) {
        throw new Error('email_not_registered');
      }

      dispatch.addNotice({
        text: messages.shareSendInviteEmailSuccess,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <MoreOptions renderInPortal={true} styleContentButtons={false}>
      {!targetUser.isAnonymous ? (
        <>
          {targetUser.roles?.includes('user') &&
          (loggedInUser?.roles?.includes('superadmin') ||
            loggedInUser?.permissions?.includes('manage-users')) ? (
            <UsersEdit
              buttonIsDropdown={true}
              handleSuccess={onUserUpdated}
              targetUser={targetUser}
            />
          ) : null}

          {targetUser.twoFactorAuthIsEnabled &&
          loggedInUser?.roles?.includes('superadmin') ? (
            <UsersDisableTwoFactorAuth
              targetUser={targetUser}
              updateUsers={onUserUpdated}
            />
          ) : null}

          {targetUser.roles?.includes('user') ? (
            <UsersSetLocked
              handleSuccess={onUserUpdated}
              targetUser={targetUser}
            />
          ) : null}

          {loggedInUser?.roles?.includes('superadmin') &&
          emailTemplatesDefault &&
          targetUser.activationKeyIsEnabled ? (
            <>
              {targetUser.email ? (
                <SendEmail
                  buttonIsDropdown={true}
                  buttonText={messages.commonSendInviteEmail}
                  emailTemplates={emailTemplates}
                  emailTemplatesDefault={emailTemplatesDefault}
                  id={`users-dropdown-send-email-${targetUser.id}`}
                  modalTitle={messages.commonSendInviteEmail}
                  onSubmit={handleSendEmail}
                  targetUser={targetUser}
                />
              ) : null}
              <CopyButton
                category="light"
                icon="link"
                isDropdown={true}
                size="small"
                style={{ display: 'block', margin: 0, padding: 0 }}
                successNoticeText={messages.shareActivationLinkCopied}
                text={messages.commonCopyActivationLink}
                valuePromise={handleCopy}
              />
            </>
          ) : null}

          {(loggedInUser?.roles?.includes('superadmin') ||
            loggedInUser?.permissions?.includes('manage-users')) &&
          targetUser?.roles?.includes('user') &&
          !targetUser?.email ? (
            <SetPassword buttonIsDropdown={true} targetUser={targetUser} />
          ) : null}

          {loggedInUser?.roles.includes('vuoadmin') &&
          targetUser?.roles?.includes('user') &&
          targetUser?.email ? (
            <UsersUpdatePasswordSet
              handleSuccess={onUserUpdated}
              targetUser={targetUser}
            />
          ) : null}
        </>
      ) : null}

      <UsersDelete
        buttonIsDropdown={true}
        handleSuccess={onUserDeleted}
        targetUser={targetUser}
      />
    </MoreOptions>
  );
};

export default UsersDropdown;
